import { reactive, watch } from 'vue'

import { DefinitionTilesOptions, DefinitionTiles, DefinitionTile, DefinitionFormField } from '@/components/types'

import { useGrid } from "./grid";

export function useTiles(options: DefinitionTilesOptions, type: string, entity: string, fields: DefinitionFormField[]): any {

  const defaultRows = 2       // default number of grid rows
  const defaultColumns = 12   // default number of grid columns

  const tiles = reactive({
    tiles: [] as Array<DefinitionTile>,
    type: "",
    entity: entity,
    dimensions: {
      rows: {
        count: defaultRows,
        height: 75,
        unit: "px",
        current: 0,
        max: 0
      },
      cols: {
        count: defaultColumns,
        width: 0,
        unit: "",
        current: 0,
        max: 0
      }
    },
    count: {
      total: 0,
      form: 0,
      chooser: 0
    }
  }) as DefinitionTiles

  const calculateTilesDimensions = (options: DefinitionTilesOptions) => {

    const rowsoverride =
      options.dimensions.rows.height !== undefined &&
      options.dimensions.rows.unit !== undefined

    tiles.dimensions.rows.count = options.dimensions.rows.count

    if (rowsoverride) {
      tiles.dimensions.rows.height = (options.dimensions.rows.height ? options.dimensions.rows.height : 100 / options.dimensions.rows.count)
      tiles.dimensions.rows.unit = (options.dimensions.rows.unit ? options.dimensions.rows.unit : "%")
    } else {
      tiles.dimensions.rows.height = 100 / options.dimensions.rows.count
      tiles.dimensions.rows.unit = "%"
    }

    const colsoverride =
      options.dimensions.cols.width !== undefined &&
      options.dimensions.cols.unit !== undefined

    tiles.dimensions.cols.count = options.dimensions.cols.count

    if (colsoverride) {
      tiles.dimensions.cols.width = (options.dimensions.cols.width ? options.dimensions.cols.width : 100 / options.dimensions.cols.count)
      tiles.dimensions.cols.unit = (options.dimensions.cols.unit ? options.dimensions.cols.unit : "%")
    } else {
      tiles.dimensions.cols.width = 100 / options.dimensions.cols.count
      tiles.dimensions.cols.unit = "%"
    }
  };

  calculateTilesDimensions(options)

  const { grid, createGrid, addToGrid } = useGrid()

  const updateDimensionsCurrent = (row: number, column: number, height: number, width: number) => {
    if (row + height > tiles.dimensions.rows.current) tiles.dimensions.rows.current = row + height
    if (column + width > tiles.dimensions.cols.current) tiles.dimensions.cols.current = column + width
  };

  const updateDimensionsMax = (row: number, column: number, height: number, width: number) => {
    if (row + height > tiles.dimensions.rows.max) tiles.dimensions.rows.max = row + height
    if (column + width > tiles.dimensions.cols.max) tiles.dimensions.cols.max = column + width
  };

  const determineLayoutMaxFromFields = () => {
    for (const field of fields) {
      updateDimensionsMax(field.layout.row, field.layout.column, field.layout.height, field.layout.width)
    }

    // we always use the defaultColumns number of columns at the moment
    tiles.dimensions.cols.max = defaultColumns
  };

  const generateTilesFromFormFieldsStatic = () => {
    determineLayoutMaxFromFields()

    const t: DefinitionTile[] = []

    tiles.dimensions.rows.current = 0
    tiles.dimensions.cols.current = 0
    tiles.type = "static"

    let packing = 1
    for (const field of fields) {
      t.push({
        id: field.layout.id,
        row: field.layout.row,
        column: field.layout.column,
        height: field.layout.height,
        width: field.layout.width,
        chooser: field.chooser.selectable,
        packing: packing++,
        entity: { type: tiles.entity, group: field.field.group && field.field.group.name, name: field.field.name }
      })

      if (!field.chooser.selectable) {
        updateDimensionsCurrent(field.layout.row, field.layout.column, field.layout.height, field.layout.width)
      }
    }

    tiles.tiles = t
  }

  const difference = (a: Set<string>, b: Set<string>) => {
    const _difference = new Set(a)
    for (const elem of b) {
      _difference.delete(elem)
    }

    return _difference
  }

  const split = (set: Set<string>): any[] => {
    const result = [] as any[]

    for (const ele of set.values()) {
      const ns = ele.split('----')

      if (ns.length === 2) {
        result.push({ group: ns[0], name: ns[1] })
      } else if (ns.length === 1) {
        result.push({ group: undefined, name: ns[0] })
      }
    }

    return result
  };

  const generateTilesFromFormFieldsDynamic = () => {

    determineLayoutMaxFromFields()

    createGrid(tiles.dimensions.rows.max + fields.length + 1, tiles.dimensions.cols.max)

    const t: DefinitionTile[] = []

    tiles.dimensions.rows.current = 0
    tiles.dimensions.cols.current = 0
    tiles.type = "dynamic"

    if (tiles.tiles.length === 0) {

      let packing = 1

      /*
       * inital form layout, add all static, non choosable, fields
       */
      for (const field of fields) {
        if (field.layout && field.chooser && !field.chooser.selectable) {
          const res = addToGrid(
            field.layout.row,
            field.layout.column,
            field.layout.height,
            field.layout.width,
            field.layout.id,
            true
          )

          if (res) {
            updateDimensionsCurrent(res.row, res.column, res.height, res.width)

            t.push({
              id: field.layout.id,
              row: res.row,
              column: res.column,
              height: res.height,
              width: res.width,
              chooser: field.chooser.selectable,
              packing: packing++,
              entity: { type: tiles.entity, group: field.field.group && field.field.group.name, name: field.field.name }
            })
          }
        }
      }

      /*
       * add all optional choosable fields, these could have been selected in menu favorites etc.
       */
      let rowMax = tiles.dimensions.rows.current

      for (const field of fields) {
        if (field.layout && field.chooser && field.chooser.selectable && field.chooser.selected) {
          const res = addToGrid(
            field.layout.row,
            field.layout.column,
            field.layout.height,
            field.layout.width,
            field.layout.id,
            false,
            rowMax
          )

          rowMax = res.row > rowMax ? res.row : rowMax

          if (res) {
            updateDimensionsCurrent(res.row, res.column, res.height, res.width)

            t.push({
              id: field.layout.id,
              row: res.row,
              column: res.column,
              height: res.height,
              width: res.width,
              chooser: false,
              packing: packing++,
              entity: { type: tiles.entity, group: field.field.group && field.field.group.name, name: field.field.name }
            })
          }
        }
      }

      tiles.tiles = t

    } else {
      /*
       * updated form layout, a field was added or removed
       */

      /* determine current maximum packing weight */
      let packingMax = 0
      for (const tile of tiles.tiles) {
        if (tile.packing && tile.packing > packingMax) packingMax = tile.packing
      }

      /* create set with fields on form names */
      const fieldsOnForm = fields.filter((field) => { return !field.chooser.selectable || (field.chooser.selectable && field.chooser.selected) })
      const fieldSet: Set<string> = new Set()
      for (const f of fieldsOnForm) {
        fieldSet.add((f.field.group && f.field.group.name) ? f.field.group.name + '----' + f.field.name : f.field.name)
      }

      /* create set with fields on tiles names */
      const tileSet: Set<string> = new Set()
      for (const t of tiles.tiles) {
        tileSet.add(t.entity.group && t.entity.group ? t.entity.group + '----' + t.entity.name : t.entity.name)
      }

      /* use set difference to determine if field was added or removed */
      const fieldsAdded = difference(fieldSet, tileSet)
      const fieldsRemoved = difference(tileSet, fieldSet)

      const fieldsAddedSplitted = split(fieldsAdded)
      const fieldsRemovedSplitted = split(fieldsRemoved)

      /* field was removed (deselected on chooser), remove from tiles */
      if (fieldsRemovedSplitted.length > 0) {
        for (const fieldRemoved of fieldsRemovedSplitted) {
          const index = tiles.tiles.findIndex((tile) => { return tile.entity.group === fieldRemoved.group && tile.entity.name === fieldRemoved.name })
          tiles.tiles.splice(index, 1)
        }
      }

      /* sort tiles by packing weight */
      tiles.tiles.sort((a: DefinitionTile, b: DefinitionTile) => {
        if (a.packing > b.packing) return 1
        if (a.packing < b.packing) return -1
        return 0
      });

      /* readd existing tiles (in packing order) to grid */
      for (const tile of tiles.tiles) {
        const fieldForTile = fields.filter((field) => {
          const fgroup = field.field.group && field.field.group.name
          return tile.entity.group === fgroup && tile.entity.name === field.field.name
        });

        if (fieldForTile.length > 0) {
          for (const fft of fieldForTile) {
            const res = addToGrid(
              tile.row,
              tile.column,
              tile.height,
              tile.width,
              fft.layout.id,
              true
            )

            fft.field.focus = false;
            if (res) {
              tile.row = res.row
              tile.column = res.column
              tile.height = res.height
              tile.width = res.width
              updateDimensionsCurrent(res.row, res.column, res.height, res.width)
            }
          }
        }
      }

      /* field was selected on chooser, add to tiles */
      if (fieldsAddedSplitted.length > 0) {
        for (const fieldAdded of fieldsAddedSplitted) {
          const fieldForTile = fields.filter((field) => {
            const fgroup = field.field.group && field.field.group.name
            return fieldAdded.group === fgroup && fieldAdded.name === field.field.name
          });

          if (fieldForTile.length > 0) {
            for (const fft of fieldForTile) {
              const res = addToGrid(
                fft.layout.row,
                fft.layout.column,
                fft.layout.height,
                fft.layout.width,
                fft.layout.id,
                true,
                tiles.dimensions.rows.max
              )

              if (res) {
                updateDimensionsCurrent(res.row, res.column, res.height, res.width)

                tiles.tiles.push({
                  id: fft.layout.id,
                  row: res.row,
                  column: res.column,
                  height: res.height,
                  width: res.width,
                  chooser: false, // fft.chooser.selected,
                  packing: ++packingMax,
                  entity: { type: tiles.entity, group: fft.field.group && fft.field.group.name, name: fft.field.name }
                })
              }
            }
          }
        }
      }
    }
  }

  const updateTilesCount = () => {
    tiles.count.total = tiles.tiles.length
    tiles.count.chooser = tiles.tiles.filter((tile) => {
      return tile.chooser
    }).length
    tiles.count.form = tiles.count.total - tiles.count.chooser
  };

  const generateTilesFromFormFields = () => {
    switch (type) {
      case "static": {
        generateTilesFromFormFieldsStatic()
        updateTilesCount()
        tiles.dimensions.rows.max++ // add one extra row to place new fields
        break;
      }
      case "dynamic": {
        generateTilesFromFormFieldsDynamic()
        updateTilesCount()
        break;
      }
    }
  };

  watch(
    tiles,
    () => {
      updateTilesCount()
    }
  );

  return {
    tiles,
    generateTilesFromFormFields,
    grid
  }
}
